import { FC } from 'react'
import { useQuery } from '@apollo/client'
import { Button, Modal, Spin, theme } from 'antd'
import { CheckCircleFilled, ExclamationCircleFilled } from '@ant-design/icons'
import Enumerable from 'linq'
import { translate, translatePlural, translateTemplate } from '../../../core'
import { dateDisplay, dateFormatSlashed, usdFormat } from '../../../core/until'
import { PurchaseOrderListQuery } from '../graphql/purchase-order.graphql'
import { PurchaseOrderCart } from './PurchaseOrderEntryCart'
import styles from './PurchaseOrderEntry.module.scss'

const MAX_NAMED_VENDORS = 6

type PurchaseOrderEntryConfirmModalProps = {
  open: boolean
  cart: PurchaseOrderCart
  generating: boolean
  onConfirm: () => void
  onCancel: () => void
}

export const PurchaseOrderEntryConfirmModal: FC<PurchaseOrderEntryConfirmModalProps> = ({
  open,
  cart,
  generating,
  onConfirm,
  onCancel,
}) => {
  const { token } = theme.useToken()
  const count = cart.groups.length
  const vendorNames = cart.groups.map(group => group.vendor.contactId)
  const vendors = vendorNames.length > MAX_NAMED_VENDORS
    ? `${vendorNames.slice(0, MAX_NAMED_VENDORS).join(', ')} ${translateTemplate('and_n_more', {
      count: vendorNames.length - MAX_NAMED_VENDORS,
    })}`
    : vendorNames.join(', ')

  return (
    <Modal
      open={open}
      centered
      width={480}
      footer={null}
      closable={!generating}
      maskClosable={!generating}
      keyboard={!generating}
      onCancel={onCancel}
    >
      <div className={styles.outcome}>
        <ExclamationCircleFilled className={styles.outcomeIcon} style={{ color: token.colorWarning }} />
        <h2 className={styles.outcomeTitle}>
          {translatePlural(count, 'po_confirm_title_one', 'po_confirm_title_many')}
        </h2>
        <p className={styles.outcomeText}>
          {translatePlural(count, 'po_confirm_body_one', 'po_confirm_body_many', {
            vendors,
            total: usdFormat(cart.total),
          })}
        </p>
        <div className={styles.outcomeActions}>
          <Button disabled={generating} onClick={onCancel}>{translate('cancel')}</Button>
          <Button type='primary' loading={generating} onClick={onConfirm}>
            {translatePlural(count, 'po_confirm_ok_one', 'po_confirm_ok_many')}
          </Button>
        </div>
      </div>
    </Modal>
  )
}

type PurchaseOrderEntrySuccessModalProps = {
  purchaseOrderIds: number[]
  onViewPurchaseOrder: (number: number) => void
  onGenerateNew: () => void
  onGoToList: () => void
}

export const PurchaseOrderEntrySuccessModal: FC<PurchaseOrderEntrySuccessModalProps> = ({
  purchaseOrderIds,
  onViewPurchaseOrder,
  onGenerateNew,
  onGoToList,
}) => {
  const { token } = theme.useToken()
  const count = purchaseOrderIds.length

  const { data, loading } = useQuery(PurchaseOrderListQuery, {
    variables: {
      request: {
        id: purchaseOrderIds,
        take: count,
        skip: 0,
      },
    },
    skip: !count,
  })

  const purchaseOrders = Enumerable.from(data?.purchaseOrdersFind.data ?? [])
    .orderBy(po => po.number)
    .toArray()

  return (
    <Modal
      open={count > 0}
      centered
      width={560}
      footer={null}
      closable={false}
      maskClosable={false}
      keyboard={false}
    >
      <div className={styles.outcome}>
        <CheckCircleFilled className={styles.outcomeIcon} style={{ color: token.colorSuccess }} />
        <h2 className={styles.outcomeTitle}>
          {translatePlural(count, 'po_success_title_one', 'po_success_title_many')}
        </h2>
        <p className={styles.outcomeText}>{translate('po_success_subtitle')}</p>

        <Spin spinning={loading}>
          <ul className={styles.resultList}>
            {purchaseOrders.map(po => (
              <li key={po.id} className={styles.resultRow}>
                <div>
                  <div className={styles.resultTitle}>
                    {translate('purchase_order_abbr')} {po.number} · {po.vendor.contactId}
                  </div>
                  <div className={styles.resultMeta}>
                    {translatePlural(po.items.length, 'po_success_line_count_one', 'po_success_line_count_many')}
                    {' · '}
                    {dateDisplay(po.date, dateFormatSlashed)}
                  </div>
                </div>
                <div className={styles.resultActions}>
                  <strong>{usdFormat(Enumerable.from(po.items).sum(it => it.price * it.quantity))}</strong>
                  <Button size='small' onClick={() => onViewPurchaseOrder(po.number)}>
                    {translate('view_po')}
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </Spin>

        <div className={styles.outcomeActions}>
          <Button onClick={onGenerateNew}>{translate('generate_new_po')}</Button>
          <Button type='primary' onClick={onGoToList}>{translate('go_to_purchase_orders')}</Button>
        </div>
      </div>
    </Modal>
  )
}
